import { useState } from 'react';
import PageHero from '@/components/feature/PageHero';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  mentorshipHero,
  mentorshipStats,
  howItWorks,
  mentorProfiles,
  mentorshipTestimonials,
  mentorshipFAQ,
} from '@/mocks/mentorship';

export default function MentorshipPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        title={mentorshipHero.title}
        subtitle={mentorshipHero.subtitle}
        imageUrl={mentorshipHero.imageUrl}
        breadcrumb={{ label: 'Home', href: '/' }}
      />

      {/* Stats */}
      <section className="py-8 md:py-10 bg-background-50 border-b border-background-200/70">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {mentorshipStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
            How It Works
          </h2>
          <p className="text-foreground-600 text-center max-w-2xl mx-auto mb-12">
            Our mentorship program follows a structured 4-step process designed to create meaningful, lasting connections.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step) => (
              <div key={step.step} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center transition-all duration-300 hover:border-accent-300/50">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className={`${step.icon} text-xl text-accent-600 w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <div className="text-xs font-bold text-accent-500 mb-2 tracking-wider">{step.step}</div>
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">{step.title}</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mentor Profiles */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
            Meet Our Mentors
          </h2>
          <p className="text-foreground-600 text-center max-w-2xl mx-auto mb-12">
            Our mentors are experienced professionals from diverse backgrounds, united by their passion for youth development.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mentorProfiles.map((mentor) => (
              <div key={mentor.name} className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex flex-col sm:flex-row gap-5 transition-all duration-300 hover:border-accent-300/50">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden flex-shrink-0 mx-auto sm:mx-0">
                  <img
                    src={mentor.imageUrl}
                    alt={mentor.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-heading font-semibold text-lg text-foreground-950">{mentor.name}</h3>
                  <p className="text-sm text-accent-600 font-medium mb-2">{mentor.role}</p>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-3">{mentor.bio}</p>
                  <div className="flex flex-wrap gap-1.5 justify-center sm:justify-start">
                    {mentor.expertise.map((exp, i) => (
                      <span key={i} className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium whitespace-nowrap">
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
            What Our Mentees Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mentorshipTestimonials.map((t, i) => (
              <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4 italic">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div>
                  <p className="font-medium text-foreground-950 text-sm">{t.name}</p>
                  <p className="text-foreground-500 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-background-100 border-t border-background-200/70">
        <div className="px-4 md:px-6 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {mentorshipFAQ.map((faq, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="font-medium text-foreground-950 text-sm md:text-base pr-4">{faq.question}</span>
                  <i className={`text-lg text-foreground-400 transition-transform duration-200 flex-shrink-0 ${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                </button>
                {openFAQ === i && (
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-accent-500">
        <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            Ready to Find Your Mentor?
          </h2>
          <p className="text-background-50/80 text-base mb-8 leading-relaxed">
            Join our next mentorship cycle and start your journey toward a brighter future. Applications are always open.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:insanperwirapenang@gmail.com"
              className="px-6 py-3 rounded-full bg-background-50 text-accent-600 font-medium text-sm hover:bg-background-100 transition-all whitespace-nowrap"
            >
              Apply Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}